import React, { useState, useEffect, useRef, useCallback } from "react";
import BundledEditor from "../Text/BundleEditor";
import rgbHex from "rgb-hex";
import * as UTILS from "../../../utils/Utils";
import { cloneDeep } from "lodash";
import { track } from "@amplitude/analytics-browser";
import { applyTableAlignment, WHITE_COLOR, getBgColorTableAttribute } from "./tableUtils";

export default function TinyEditor(props) {
  const [key, setKey] = useState(0);
  const tableWrapperId = "table-wrapper-" + props.ID;
  const editorRef = useRef(null);
  const tableColumnSticky = props.element.mobileStyle.tableColumnSticky || props.element.mobileStyle.mobileTableColumnSticky;
  const tableHeaderSticky = props.element.mobileStyle.tableHeaderSticky || props.element.mobileStyle.mobileTableHeaderSticky;
  const justifyContent =  'flex-start'; //|| props.element.mobileStyle?.justifyContent;

  const tableRefInitialValues = {
    isActive: false,
    isOpenWindow: false,
    isContextmenu: false,
  }
  const tableRef = useRef(tableRefInitialValues);

  useEffect(() => {
    dragElement(document.getElementById("tinyToolbar" + props.ID));

    function dragElement(elmnt) {
      var pos1 = 0,
        pos2 = 0,
        pos3 = 0,
        pos4 = 0;
      if (document.getElementById(elmnt.id)) {
        // if present, the header is where you move the DIV from:
        document.getElementById(elmnt.id).onmousedown = dragMouseDown;
      } else {
        // otherwise, move the DIV from anywhere inside the DIV:
        elmnt.onmousedown = dragMouseDown;
      }

      function dragMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        e = e || window.event;

        // get the mouse cursor position at startup:
        pos3 = e.clientX;
        pos4 = e.clientY;
        document.onmouseup = closeDragElement;
        // call a function whenever the cursor moves:
        document.onmousemove = elementDrag;
      }

      function elementDrag(e) {
        e.preventDefault();
        e.stopPropagation();
        e = e || window.event;

        // calculate the new cursor position:
        pos1 = pos3 - e.clientX;
        pos2 = pos4 - e.clientY;
        pos3 = e.clientX;
        pos4 = e.clientY;
        // set the element's new position:
        elmnt.style.top = elmnt.offsetTop - pos2 + "px";
        elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
      }

      function closeDragElement() {
        // stop moving when mouse button is released:
        document.onmouseup = null;
        document.onmousemove = null;
      }
    }
  }, []);

    // Handle transparent background colors for sticky columns and headers
    useEffect(() => {
      if (!editorRef.current) return;
  
      const editor = editorRef.current;
      const table = editor.dom.select('table')[0];
  
      if (!table) return;
  
      if (tableColumnSticky) {
        // Get all first column cells (td elements in the first column)
        const firstColumnCells = editor.dom.select('table tr td:first-child');
        firstColumnCells.forEach(cell => {
          const backgroundColor = editor.dom.getStyle(cell, 'background-color');
          // Check if the cell background is transparent
          const isTransparent = backgroundColor === 'transparent' ||
            backgroundColor === 'rgba(0, 0, 0, 0)' ||
            backgroundColor === 'rgba(0,0,0,0)' ||
            backgroundColor === '';
  
          // If the cell is transparent, apply white background using the existing style pattern
          if (isTransparent) {
            const existingStyle = cell.getAttribute('style') || '';
            const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
            const newStyle = `${cleanedStyle} background-color: ${WHITE_COLOR};`.trim();
            cell.setAttribute('style', newStyle);
            cell.setAttribute('data-mce-style', newStyle);
            // Notify TinyMCE of the DOM change
            editor.nodeChanged();
          }
        });
      }
  
      if (tableHeaderSticky) {
        // Get all header row cells (td elements in the first row)
        const headerRowCells = editor.dom.select('table tr:first-child td');
        headerRowCells.forEach(cell => {
          const backgroundColor = editor.dom.getStyle(cell, 'background-color');
          // Check if the cell background is transparent
          const isTransparent = backgroundColor === 'transparent' ||
            backgroundColor === 'rgba(0, 0, 0, 0)' ||
            backgroundColor === 'rgba(0,0,0,0)' ||
            backgroundColor === '';
  
          // If the cell is transparent, apply white background using the existing style pattern
          if (isTransparent) {
            const existingStyle = cell.getAttribute('style') || '';
            const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
            const newStyle = `${cleanedStyle} background-color: ${WHITE_COLOR};`.trim();
            cell.setAttribute('style', newStyle);
            cell.setAttribute('data-mce-style', newStyle);
            // Notify TinyMCE of the DOM change
            editor.nodeChanged();
          }
        });
      }
  
    }, [tableColumnSticky, tableHeaderSticky, editorRef.current]);
  

  useEffect(() => {
    setKey(key + 1);
  }, [props.preset, tableHeaderSticky, tableColumnSticky, justifyContent]);

  const handleInit = useCallback((evt, editor) => {
    // set init fn for editor
    editorRef.current = editor;

    // INITIALIZE OVERFLOW X
    const table = editor.dom.select('table')[0];
    const initialContainer = document.getElementById(tableWrapperId);
    if (!table || !initialContainer) {
      return;
    }
    const computedStyle = window.getComputedStyle(table);
    const initialTableWidth = parseInt(computedStyle.width) || 0;
    const initialContainerWidth = initialContainer.offsetWidth || 0;
    if (initialTableWidth > initialContainerWidth) {
      initialContainer.style.overflowX = 'auto';
    } else {
      initialContainer.style.overflowX = 'hidden';
    }

    // END INITIALIZE OVERFLOW X

    //config editor window / popups manager options
    const originalOpen = editor.windowManager.open;
    editor.windowManager.open = (config, params) => {
      if (config.title === 'Row Properties' || config.title === 'Cell Properties' || config.title === 'Table Properties') {
        if (config.body && config.body.type === 'tabpanel' && config.body.tabs) {
          const updatedTabs = config.body.tabs.map((tab, index) => {
            if (index === 0 && tab.name === 'general') { // "General" Tab : hide Row / Cell Type input
              const updatedItems = tab.items.filter((item) => item.label !== 'Row type' && item.label !== 'Cell type');
              return {
                ...tab,
                items: updatedItems,
              };
            }

            if (tab.name === 'advanced') { // "General" Tab : hide Row / Cell Type input
              const updatedItems = tab.items
                .filter((item) => item.label !== 'Border style')

              return {
                ...tab,
                items: updatedItems,
              };
            }
            return tab;
          });
          const newConfig = {
            ...config,
            body: {
              ...config.body,
              tabs: updatedTabs,
            },
          };
          return originalOpen.call(editor.windowManager, newConfig, params);
        }
      }
      return originalOpen.call(editor.windowManager, config, params);
    };

  }, []);

  // const preProcessContent = (content) => {
  //   const processedContent = applyTableAlignment(content, justifyContent);
  //   return processedContent;
  // }

  return (
    <>
      <div
        className="tinyMCE-Table-Draggable-Toolbar-Container"
        id={"tinyToolbar" + props.ID}
      ></div>

      <BundledEditor
        key={key}
          initialValue={props.mobileContent}
        onInit={(evt, editor) => handleInit(evt, editor)}
        init={{
          promotion: false,
          inline: true,
          selector: `#${props.ID}`,
          menubar: "",
          plugins:
            "table advtable lists advlist link code powerpaste emoticons charmap",
          placeholder: "Click here to build table...",
          font_size_input_default_unit: "px",
          font_size_formats:
            "8px 9px 10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 22px 24px 26px 28px 30px 32px 36px 40px 44px 48px 52px 56px 60px 64px 68px 72px",
          toolbar: [
            "table undo redo customInsertButton  styles fontfamily  fontsizeinput bold italic underline",
            "  backcolor forecolor align lineheight  link  bullist numlist outdent indent | subscript superscript emoticons charmap code |tabledelete  tableprops tablecellprops tablerowprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol  advtablesort advtablerownumbering",
          ],
          powerpaste_allow_local_images: true,
          powerpaste_word_import: "prompt",
          powerpaste_html_import: "prompt",
          powerpaste_googledocs_import: "prompt",
          table_resize_bars: true,
          table_toolbar: "",
          table_tab_navigation: true,
          // toolbar_persist: true,
          paste_block_drop: true,
          table_border_styles: [
            { title: "Solid", value: "solid" },
            { title: "Dotted", value: "dotted" },
            { title: "Dashed", value: "dashed" },
            { title: "Double", value: "double" },
            { title: "Groove", value: "groove" },
            { title: "Ridge", value: "ridge" },
            { title: "Inset", value: "inset" },
            { title: "Outset", value: "outset" },
            { title: "None", value: "none" },
            { title: "Hidden", value: "hidden" },
          ],
          fixed_toolbar_container: "#" + "tinyToolbar" + props.ID,
          link_list: props.pagesList,
          link_default_target: "_blank",
          link_default_protocol: "http",
          table_default_styles: {
            "border-color": "#939BA3",
            "border-style": "solid",
            "border-width": ".5px",
            "border-collapse": "separate",
            "border-spacing": "0",
            "background-color": "#FFFFFF",
          },
          table_default_attributes: {
            cellPadding: "10px",
            cellBorder: "1px solid #939BA3",
            border: ".5px",
            borderColor: "#939BA3",
            borderStyle: "solid",
          },
          object_resizing: true,
          content_style: (() => {
            const wrapperId = `#table-wrapper-${props.ID}`;

            // Base table styles
            const baseTableStyles = `
              ${wrapperId} table { 
                border-collapse: separate !important; 
                border-spacing: 0 !important;
              }
            `;

            // Case 1: No sticky (both false)
            if (!tableHeaderSticky && !tableColumnSticky) {
              return baseTableStyles;
            }

            // Case 2: Only header sticky
            if (tableHeaderSticky && !tableColumnSticky) {
              return `
                ${baseTableStyles}
                ${wrapperId} table tr:first-child {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
                ${wrapperId} table tr:first-child td {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
              `;
            }

            // Case 3: Only column sticky
            if (!tableHeaderSticky && tableColumnSticky) {
              return `
                ${baseTableStyles}
                ${wrapperId} table tr td:first-child {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 5 !important;
                  box-shadow: 2px 0 4px -2px rgba(0,0,0,0.1) !important;
                }
                /* Handle merged cells in first column - ensure they stay above other columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  z-index: 6 !important;
                }
                /* Ensure all cells in first column have proper z-index for merged scenarios */
                ${wrapperId} table tr:nth-child(n+1) td:first-child {
                  z-index: 5 !important;
                }
                /* For merged cells, ensure subsequent rows don't interfere */
                ${wrapperId} table tr td:first-child[rowspan] + tr td:first-child {
                  z-index: 4 !important;
                }
                /* Additional rule to ensure merged cells maintain z-index over adjacent columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 6 !important;
                }
              `;
            }

            // Case 4: Both header and column sticky
            if (tableHeaderSticky && tableColumnSticky) {
              return `
                ${baseTableStyles}
                ${wrapperId} table tr:first-child {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
                ${wrapperId} table tr:first-child td {
                  position: sticky !important;
                  top: 0 !important;
                  z-index: 10 !important;
                }
                ${wrapperId} table tr td:first-child {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 5 !important;
                  box-shadow: 2px 0 4px -2px rgba(0,0,0,0.1) !important;
                }
                ${wrapperId} table tr:first-child td:first-child {
                  position: sticky !important;
                  top: 0 !important;
                  left: 0 !important;
                  z-index: 15 !important;
                  box-shadow: 2px 0 4px -2px rgba(0,0,0,0.1) !important;
                }
                /* Handle merged cells in first column when both sticky - ensure they stay above other columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  z-index: 6 !important;
                }
                /* Ensure proper z-index for merged cells in header row */
                ${wrapperId} table tr:first-child td:first-child[rowspan] {
                  z-index: 16 !important;
                }
                /* For merged cells, ensure subsequent rows don't interfere */
                ${wrapperId} table tr td:first-child[rowspan] + tr td:first-child {
                  z-index: 4 !important;
                }
                /* Additional rule to ensure merged cells maintain z-index over adjacent columns */
                ${wrapperId} table tr td:first-child[rowspan] {
                  position: sticky !important;
                  left: 0 !important;
                  z-index: 6 !important;
                }
                /* Special handling for merged cells in header row when both sticky */
                ${wrapperId} table tr:first-child td:first-child[rowspan] {
                  position: sticky !important;
                  top: 0 !important;
                  left: 0 !important;
                  z-index: 16 !important;
                }
              `;
            }

            // Default fallback
            return baseTableStyles;
          })(),

          line_height_formats:
            "1 1.1 1.2 1.25 1.3 1.4 1.4 1.4 1.4 1.4 1.4 1.4 1.5 2",
          // newline_behavior:"linebreak",
          color_map: props.preset
            ? (() => {
              let thing = cloneDeep(props.preset);
              return thing.reduce((acc, curr, index) => {
                acc.push(curr.replace(/#\b/g, ""));
                // Insert the element only if it's not the last element

                acc.push("");

                return acc;
              }, []);
            })()
            : ["ffffff", "black", "white", "00000"],
          cleanup: true,
          font_family_formats: UTILS.googleFontsEditor,
          statusbar: true,
          elementpath: true,
          forced_root_block: "div",
          forced_root_block_attrs: {
            style: `font-family:${props.selectedFont}; font-size:14px;`,
          },
          browser_spellcheck: true,
          link_default_protocol: "http",

          formats: {
            removeformat: [
              {
                selector:
                  "h1,h2,h3,h4,strong,b,em,i,span,p,underline,ul,ol,li,sub,sup",
                remove: "all",

                deep: true,
              },
            ],
            list: { block: "li" },
            // unknown: { block: "span" },
            none: {
              title: "None",
              block: "div",
              selector: "impossible",
              // styles: {
              //   color: "",
              //   backgroundColor: "",
              //   fontFamily: props.selectedFont,
              //   fontSize: "14px",
              //   paddingBottom: "",
              //   fontStyle: "",
              //   fontWeight: "",
              //   lineHeight: "",
              //   textDecoration: "",
              // },
              remove: "all",
            },
            noneForRemoveFormat: {
              title: "None",
              block: "div",
              selector: "impossible",
              styles: {
                color: "",
                backgroundColor: "",
                fontFamily: props.selectedFont,
                fontSize: "14px",
                paddingBottom: "",
                fontStyle: "",
                fontWeight: "",
                lineHeight: "",
                textDecoration: "",
              },
              remove: "all",
            },
            p_all: {
              block: "p",
              selector: "impossible",
            },
            paragraph: {
              title: "Paragraph",
              block: "p",
              styles: {
                color: props.elementsFont.p.color
                  ? "#" +
                  rgbHex(`
            ${props.elementsFont.p.color.r},
            ${props.elementsFont.p.color.g},
            ${props.elementsFont.p.color.b}
            `)
                  : "#000000",
                fontFamily: props.elementsFont.p.font_family
                  ? props.elementsFont.p.font_family
                  : "Montserrat",
                fontSize: props.elementsFont.p.font_size
                  ? props.elementsFont.p.font_size + "px"
                  : "15px",
                paddingBottom: props.elementsFont.p.margin_bottom
                  ? props.elementsFont.p.margin_bottom + "px"
                  : "0px",

                fontStyle: props.elementsFont.p.font_style || "",
                fontWeight: props.elementsFont.p.font_weight || "",
                lineHeight: props.elementsFont.p.line_height || "",
                textDecoration: props.elementsFont.p.text_decoration || "",
                marginBottom: "0px",
              },

              deep: true,
              exact: true,
              remove: "all",
              merge_with_parents: false,
              merge_siblings: false,
            },
            h1_all: {
              block: "h1",
              selector: "impossible",
            },
            h1: {
              title: "Header 1",
              block: "h1",
              styles: {
                color: props.elementsFont.h1.color
                  ? "#" +
                  rgbHex(`
            ${props.elementsFont.h1.color.r},
            ${props.elementsFont.h1.color.g},
            ${props.elementsFont.h1.color.b}
            `)
                  : "",
                fontFamily: props.elementsFont.h1.font_family
                  ? props.elementsFont.h1.font_family
                  : "Open Sans",
                fontSize: props.elementsFont.h1.font_size + "px" || "36px",
                paddingBottom: props.elementsFont.h1.margin_bottom
                  ? props.elementsFont.h1.margin_bottom + "px"
                  : "0px",
                backgroundColor:
                  props.elementsFont.h1.background_color &&
                    props.elementsFont.h1.background_color.a !== 0
                    ? "#" +
                    rgbHex(`
          ${props.elementsFont.h1.background_color.r},
          ${props.elementsFont.h1.background_color.g},
          ${props.elementsFont.h1.background_color.b}
          `)
                    : "",
                fontStyle: props.elementsFont.h1.font_style || "",
                fontWeight: props.elementsFont.h1.font_weight || "",
                lineHeight: props.elementsFont.h1.line_height || "",
                textDecoration: props.elementsFont.h1.text_decoration || "",
                marginBottom: "0px",
              },
              deep: true,
              exact: true,
              merge_with_parents: false,
              merge_siblings: false,
              remove: "all",
            },
            h2_all: {
              block: "h2",
              selector: "impossible",
            },
            h2: {
              title: "Header 2",
              block: "h2",
              styles: {
                color: props.elementsFont.h2.color
                  ? "#" +
                  rgbHex(`
            ${props.elementsFont.h2.color.r},
            ${props.elementsFont.h2.color.g},
            ${props.elementsFont.h2.color.b}
            `)
                  : "",
                fontFamily: props.elementsFont.h2.font_family || "Open Sans",
                fontSize: props.elementsFont.h2.font_size + "px" || "32px",
                paddingBottom: props.elementsFont.h2.margin_bottom
                  ? props.elementsFont.h2.margin_bottom + "px"
                  : "0px",
                backgroundColor:
                  props.elementsFont.h2.background_color &&
                    props.elementsFont.h2.background_color.a !== 0
                    ? "#" +
                    rgbHex(`
            ${props.elementsFont.h2.background_color.r},
            ${props.elementsFont.h2.background_color.g},
            ${props.elementsFont.h2.background_color.b}
            `)
                    : "",
                fontStyle: props.elementsFont.h2.font_style || "",
                fontWeight: props.elementsFont.h2.font_weight || "",
                lineHeight: props.elementsFont.h2.line_height || "",
                textDecoration: props.elementsFont.h2.text_decoration || "",
                marginBottom: "0px",
              },
              deep: true,
              exact: true,
              remove: "all",
              merge_with_parents: false,
              merge_siblings: false,
            },
            h3_all: {
              block: "h3",
              selector: "impossible",
            },
            h3: {
              title: "Header 3",
              block: "h3",
              styles: {
                color: props.elementsFont.h3.color
                  ? "#" +
                  rgbHex(`
            ${props.elementsFont.h3.color.r},
            ${props.elementsFont.h3.color.g},
            ${props.elementsFont.h3.color.b}
            `)
                  : "",
                fontFamily: props.elementsFont.h3.font_family || "Open Sans",
                fontSize: props.elementsFont.h3.font_size + "px" || "28px",
                paddingBottom: props.elementsFont.h3.margin_bottom
                  ? props.elementsFont.h3.margin_bottom + "px"
                  : "0px",
                backgroundColor:
                  props.elementsFont.h3.background_color &&
                    props.elementsFont.h3.background_color.a !== 0
                    ? "#" +
                    rgbHex(`
            ${props.elementsFont.h3.background_color.r},
            ${props.elementsFont.h3.background_color.g},
            ${props.elementsFont.h3.background_color.b}
            `)
                    : "",
                fontStyle: props.elementsFont.h3.font_style || "",
                fontWeight: props.elementsFont.h3.font_weight || "",
                lineHeight: props.elementsFont.h3.line_height || "",
                textDecoration: props.elementsFont.h3.text_decoration || "",
                marginBottom: "0px",
              },
              deep: true,
              exact: true,
              remove: "all",
              merge_with_parents: false,
              merge_siblings: false,
            },
            h4_all: { block: "h4", selector: "impossible" },
            h4: {
              title: "Header 4",
              block: "h4",
              styles: {
                color: props.elementsFont.h4.color
                  ? "#" +
                  rgbHex(`
          ${props.elementsFont.h4.color.r},
          ${props.elementsFont.h4.color.g},
          ${props.elementsFont.h4.color.b}
          `)
                  : "",
                fontFamily: props.elementsFont.h4.font_family || "Open Sans",
                fontSize: props.elementsFont.h4.font_size + "px" || "0px",
                paddingBottom: props.elementsFont.h4.margin_bottom
                  ? props.elementsFont.h4.margin_bottom + "px"
                  : "0px",
                backgroundColor:
                  props.elementsFont.h4.background_color &&
                    props.elementsFont.h4.background_color.a !== 0
                    ? "#" +
                    rgbHex(`
            ${props.elementsFont.h4.background_color.r},
            ${props.elementsFont.h4.background_color.g},
            ${props.elementsFont.h4.background_color.b}
            `)
                    : "",
                fontStyle: props.elementsFont.h4.font_style || "",
                fontWeight: props.elementsFont.h4.font_weight || "",
                lineHeight: props.elementsFont.h4.line_height || "",
                textDecoration: props.elementsFont.h4.text_decoration || "",
                marginBottom: "0px",
              },
              deep: true,
              exact: true,
              remove: "all",

              merge_with_parents: false,
              merge_siblings: false,
            },
          },
          style_formats: [
            { title: "None", format: "none" },
            { title: "Paragraph", format: "paragraph" },
            { title: "Header 1", format: "h1" },
            { title: "Header 2", format: "h2" },
            { title: "Header 3", format: "h3" },
            { title: "Header 4", format: "h4" },
            { title: "Paragraph *", format: "p_all" },
            { title: "Header 1 *", format: "h1_all" },
            { title: "Header 2 *", format: "h2_all" },
            { title: "Header 3 *", format: "h3_all" },
            { title: "Header 4 *", format: "h4_all" },
          ],

          setup: (editor) => {
            let pivotRow = null;
            let lastSelectedNode = null;
            let windowOpen = null;
            editor.on('NodeChange', (e) => {
              // Guardar la fila actual donde está el cursor
              const selectedNode = editor.selection.getNode();
              lastSelectedNode = selectedNode;
              pivotRow = selectedNode.closest('td');
            });
            editor.on('TableModified', (e) => {
              const { table, structure, style } = e;

              // START STRUCTURE CHANGES
              const computedStyle = window.getComputedStyle(table);
              const tableWidth = parseInt(computedStyle.width) || 0;
              const container = document.getElementById(tableWrapperId);
              if (tableWidth > container.offsetWidth) {
                container.style.overflowX = 'auto';
              } else {
                container.style.overflowX = 'hidden';
              }
              const { isActive, isContextmenu, isOpenWindow } = tableRef;
              if (isActive && !isContextmenu && !isOpenWindow) {
                const resizersContainer = getResizerContainer(props.ID);
                resizersContainer.className = 'show-resizer';
              }
              // END STRUCTURE CHANGES


              if (style) {
                //Inferred modified row from selection
                // if row is modified, update all td background color in the row modified
                if (windowOpen === 'mceTableRowProps') {
                  setTimeout(() => {
                    const currentSelection = editor.selection.getNode();
                    const currentTr = currentSelection.closest('tr');
                    const trBackgroundColor = currentTr.style.backgroundColor;
                    if (currentTr) {
                      // update all td background color in the row modified
                      const allTds = currentTr.querySelectorAll('td');
                      allTds.forEach(td => {
                        const existingStyle = td.getAttribute('style') || '';
                        const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                        td.setAttribute('style', `${cleanedStyle} background-color: ${trBackgroundColor};`);
                        td.setAttribute('data-mce-style', `${cleanedStyle} background-color: ${trBackgroundColor};`);
                      });
                      windowOpen = null;
                    }
                  }, 0);
                }
              }
            });
            editor.on('ExecCommand', (e) => {
              if (e.command === 'mceTableRowProps') {
                windowOpen = 'mceTableRowProps';
              }
              if (e.command === 'mceTableCellProps') {
                windowOpen = 'mceTableCellProps';
              }
              if (e.command === 'mceTableInsertRowAfter' || e.command === 'mceTableInsertRowBefore') {
                if (pivotRow) {
                  const currentTr = pivotRow.closest('tr');
                  const currentSelection = editor.selection.getNode();
                  const newTr = currentSelection.closest('tr');
                  const trBackgroundColor = currentTr.style.backgroundColor;

                  if (trBackgroundColor) {
                    // Applied color to all td in the new row for consistency
                    const allTds = newTr.querySelectorAll('td');
                    allTds.forEach(td => {
                      const existingStyle = td.getAttribute('style') || '';
                      const cleanedStyle = existingStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                      td.setAttribute('style', `${cleanedStyle} background-color: ${trBackgroundColor};`);
                      td.setAttribute('data-mce-style', `${cleanedStyle} background-color: ${trBackgroundColor};`);
                    });

                    // Apply background color to the new row itself
                    const existingTrStyle = newTr.getAttribute('style') || '';
                    const cleanedTrStyle = existingTrStyle.replace(/background-color\s*:\s*[^;]+;?/i, '').trim();
                    newTr.setAttribute('style', `${cleanedTrStyle} background-color: ${trBackgroundColor};`);
                    newTr.setAttribute('data-mce-style', `${cleanedTrStyle} background-color: ${trBackgroundColor};`);
                  }
                }
              }
            });

            editor.on('BeforeExecCommand', (e) => {
              if (e.command === 'mceToggleFormat') {
                lastSelectedNode.removeAttribute('style');
                lastSelectedNode.removeAttribute('data-mce-style');
              }
            });
            editor.on("OpenWindow", () => {
              const elements = document.getElementsByClassName(
                "ephox-snooker-resizer-bar"
              );
              [...elements].map((element) => {
                element.style.display = "none";
              });
            });

            editor.on("contextmenu", () => {
              const elements = document.getElementsByClassName(
                "ephox-snooker-resizer-bar"
              );
              [...elements].map((element) => {
                element.style.display = "none";
              });
            });
            editor.on("blur", () => {
              const content = editorRef.current.getContent();

              if (content !== props.mobileContent) {
                function emojiToUnicode(text) {
                  return text.replace(
                    /([\u{1F600}-\u{1F64F}])|([\u{1F300}-\u{1F5FF}])|([\u{1F680}-\u{1F6FF}])|([\u{2600}-\u{26FF}])|([\u{2700}-\u{27BF}])|([\u{1F900}-\u{1F9FF}])|([\u{1F1E0}-\u{1F1FF}])/gu,
                    (match) => {
                      return (
                        "&#x" +
                        match
                          .codePointAt(0)
                          .toString(16)
                          .toUpperCase()
                          .padStart(4, "0")
                      );
                    }
                  );
                }
                const editorContent = editorRef.current.getContent();
                const content = editorContent
                  .replace(/border-collapse:\s*collapse;/gi, 'border-collapse: separate;')
                  .replace(/border-spacing:\s*[^;]+;/gi, 'border-spacing: 0px !important;');

                const finalContent = /border-spacing:\s*0px;/.test(content)
                  ? content
                  : content.replace(/(<table[^>]*style="[^"]*)/gi, '$1 border-spacing: 0px !important;');
                const encodedHTMLContent = emojiToUnicode(finalContent);
                let layout = cloneDeep(props.layout);
                const newLayout = UTILS.UpdateLayout(
                  layout,
                  props.ID,
                  (item) => {
                    item.mobileStyle.content = encodedHTMLContent;
                    if (props.isMirrored) {
                      item.desktopStyle.content = encodedHTMLContent;
                    }
                  },
                  (colItem) => {
                    colItem.mobileStyle.content = encodedHTMLContent;
                    if (props.isMirrored) {
                      colItem.desktopStyle.content = encodedHTMLContent;
                    }
                  },
                  (colItemNested) => {
                    colItemNested.mobileStyle.content = encodedHTMLContent;
                    if (props.isMirrored) {
                      colItemNested.desktopStyle.content = encodedHTMLContent;
                    }
                  }
                );

                props.layoutArray(props.layoutTitle, newLayout);
              }
            });
            editor.on("focus", (e) => {
              if (
                document
                  .getElementById("table-wrapper-" + props.ID)
                  .getBoundingClientRect().x <
                window.innerWidth / 2
              ) {
                document.getElementById("tinyToolbar" + props.ID).style.right =
                  "inherit";
                document.getElementById("tinyToolbar" + props.ID).style.left =
                  "0px";
              } else {
                document.getElementById("tinyToolbar" + props.ID).style.left =
                  "inherit";
                document.getElementById("tinyToolbar" + props.ID).style.right =
                  "0px";
              }
              if (
                document
                  .getElementById("table-wrapper-" + props.ID)
                  .getBoundingClientRect().y < 162
              ) {
                //   -document.getElementById(props.ID).getBoundingClientRect().y
                // );
                document.getElementById("tinyToolbar" + props.ID).style.top =
                  -document
                    .getElementById("table-wrapper-" + props.ID)
                    .getBoundingClientRect().y + "px";
              } else {
                document.getElementById("tinyToolbar" + props.ID).style.top =
                  "-125px";
              }
            });

            editor.on("init", (e) => {
              document.addEventListener("touchmove", function (event) { }, {
                passive: true,
              });
              const contentBody = editor.getBody();
              if (contentBody) {
                contentBody.style.display = 'flex';
                contentBody.style.justifyContent = justifyContent || 'flex-start';
              }
            });

            editor.on("dragover", function () {
              tinymce
                .get()
                .forEach((instance) => instance.mode.set("readonly"));
            });
            editor.on("OpenWindow", () => {
              // Use a Map to store removed classes with elements as keys
              let removedClassesMap = new Map();

              function removeDragHandleClasses() {
                const elements = document.querySelectorAll(
                  '[class*="dragHandle"]'
                );
                elements.forEach((element) => {
                  // Store removed classes for this element
                  const removedClasses = [];
                  element.classList.forEach((className) => {
                    if (className.includes("dragHandle")) {
                      removedClasses.push(className);
                      element.classList.remove(className);
                    }
                  });
                  // Store the removed classes in the map
                  if (removedClasses.length > 0) {
                    removedClassesMap.set(element, removedClasses);
                  }
                });
              }

              // Remove drag handle classes on mount
              removeDragHandleClasses();

              // Restore drag handle classes on cleanup
              editor.on("CloseWindow", () => {
                function restoreDragHandleClasses() {
                  removedClassesMap.forEach((removedClasses, element) => {
                    removedClasses.forEach((className) => {
                      element.classList.add(className);
                    });
                  });
                  // Clear the map after restoring
                  removedClassesMap.clear();
                }
                restoreDragHandleClasses();
              });
            });
            editor.ui.registry.addButton("customInsertButton", {
              tooltip: "Clear Formatting",
              icon: "remove-formatting",
              onAction: function (e) {
                tinymce.activeEditor.formatter.apply("noneForRemoveFormat");
                editor.execCommand("removeformat");
              },
            });
            editor.on("FormatApply", (e) => {
              e.format === "alignleft" ||
                e.format === "alignright" ||
                e.format == "aligncenter" ||
                e.format == "alignjustify"
                ? track("Table Text Alignment Changed", {
                  alignment: e.format,
                })
                : "";
            });

            editor.on("OpenWindow", (e) => {
              tableRef.current.isOpenWindow = true;
              const dialog = e.dialog;

              // Disable scrolling when OpenWindow is on
              const gridLinesDiv = document.querySelector('.gridLines.mobileView');
              if (gridLinesDiv) {
                gridLinesDiv.style.setProperty('overflow-y', 'hidden', 'important');
              }

              // set default colors
              if (dialog && dialog.getData()) {
                const dialogData = dialog.getData();
                const isTableProperties = dialogData.hasOwnProperty('width') && dialogData.hasOwnProperty('height') && dialogData.hasOwnProperty('cellspacing'); // Table Properties has width, height, and cellspacing
                const isCellProperties = dialogData.hasOwnProperty('backgroundcolor') && !dialogData.hasOwnProperty('height') && !dialogData.hasOwnProperty('cellspacing'); // Cell Properties has backgroundcolor but not height or cellspacing
                const isRowProperties = dialogData.hasOwnProperty('height') && !dialogData.hasOwnProperty('cellspacing'); // Row Properties has height but not cellspacing
                
                if (isTableProperties) {
                  // Handle Table Properties
                  const table = editor.dom.select('table')[0];
                  if (table) {
                    const bgColor = getBgColorTableAttribute(table);
                    const backgroundColor = bgColor || '';

                    dialog.setData({
                      ...dialogData,
                      backgroundcolor: backgroundColor,
                    });
                  }
                } else if (isCellProperties) {
                  // Set background color to blue for Cell Properties
                  dialog.setData({
                    ...dialogData,
                    bordercolor: '', // Red
                    backgroundcolor: '', // Red
                  });
                } else if (isRowProperties && lastSelectedNode) {
                  // Obtener la fila desde lastSelectedNode (que es un <td>)
                  const tr = lastSelectedNode.closest('tr');
                  let rowBackgroundColor = '#FFFFFF'; // Color por defecto (blanco)

                  if (tr) {
                    // Obtener la tabla
                    const table = tr.closest('table');
                    if (table) {
                      // Obtener todas las filas de la tabla
                      const allRows = table.querySelectorAll('tr');
                      // Verificar si la fila seleccionada es la primera
                      const isFirstRow = allRows[0] === tr;
                      // Asignar color dinámico
                      rowBackgroundColor = isFirstRow ? '#455B73' : '#FFFFFF';
                    }
                  }

                  // Set background color dinámicamente para Row Properties
                  dialog.setData({
                    ...dialogData,
                    bordercolor: '#939BA3',
                    backgroundcolor: rowBackgroundColor // Color asignado dinámicamente
                  });
                }
              }

              document.getElementById("tinyToolbar" + props.ID).onmousedown =
                null;
              document.getElementById("tinyToolbar" + props.ID).onmouseup =
                null;
              document.getElementById("tinyToolbar" + props.ID).onmousemove =
                null;
            });
            editor.on("CloseWindow", () => {
              tableRef.current.isOpenWindow = false;
              tableRef.current.isContextmenu = false;

              // Enable scrolling when OpenWindow is off
              const gridLinesDiv = document.querySelector('.gridLines.mobileView');
              if (gridLinesDiv) {
                gridLinesDiv.style.setProperty('overflow-y', 'scroll', 'important');
              }

              dragElement(document.getElementById("tinyToolbar" + props.ID));

              function dragElement(elmnt) {
                var pos1 = 0,
                  pos2 = 0,
                  pos3 = 0,
                  pos4 = 0;
                if (document.getElementById(elmnt.id)) {
                  // if present, the header is where you move the DIV from:
                  document.getElementById(elmnt.id).onmousedown = dragMouseDown;
                } else {
                  // otherwise, move the DIV from anywhere inside the DIV:
                  elmnt.onmousedown = dragMouseDown;
                }

                function dragMouseDown(e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e = e || window.event;

                  // get the mouse cursor position at startup:
                  pos3 = e.clientX;
                  pos4 = e.clientY;
                  document.onmouseup = closeDragElement;
                  // call a function whenever the cursor moves:
                  document.onmousemove = elementDrag;
                }

                function elementDrag(e) {
                  e.preventDefault();
                  e.stopPropagation();
                  e = e || window.event;

                  // calculate the new cursor position:
                  pos1 = pos3 - e.clientX;
                  pos2 = pos4 - e.clientY;
                  pos3 = e.clientX;
                  pos4 = e.clientY;
                  // set the element's new position:
                  elmnt.style.top = elmnt.offsetTop - pos2 + "px";
                  elmnt.style.left = elmnt.offsetLeft - pos1 + "px";
                }

                function closeDragElement() {
                  // stop moving when mouse button is released:
                  document.onmouseup = null;
                  document.onmousemove = null;
                }
              }
            });

            document
              .getElementById(editor.id)
              .addEventListener("drop", function (e) {
                // e.preventDefault();
                // e.stopImmediatePropagation();

                tinymce
                  .get()
                  .forEach((instance) => instance.mode.set("design"));
              });
            document.addEventListener("dragend", function (e) {
              e.preventDefault();
              e.stopImmediatePropagation();
              tinymce.get().forEach((instance) => instance.mode.set("design"));
            });
          },
        }}
      />
    </>
  );
}
